/**
 * Centralized help text registry for alert filter forms
 * Provides contextual tooltips for all selectable labels and options
 */

export const HELP_TEXT: Record<string, string> = {
  // Field labels
  "field.type": "Select whether you want to buy (WTB), sell (WTS), or see both in your alerts.",
  "field.categories": "Choose product families you care about (e.g., Drones, Batteries, Motors).",
  "field.manufacturers": "Specify brands you're interested in to narrow down results.",
  "field.models": "Add specific model names to find exact products you need.",
  "field.partNumbers.exact": "Enter complete part numbers for precise matching.",
  "field.partNumbers.prefix": "Use partial part numbers to catch related items.",
  "field.condition": "Filter by item condition to match your sourcing policy.",
  "field.quantity": "Set minimum/maximum quantities to separate retail from bulk deals.",
  "field.quantity.bulkOnly": "Show only listings at or above the bulk threshold (e.g., 10+ units).",
  "field.price": "Limit results by price; currency shown is the listing currency.",
  "field.price.ignoreIfMissing": "Include items even if price is not listed.",
  "field.country": "Choose a single country to focus results. Leave empty for any.",
  "field.keywords.include":
    "Match broadcasts containing these terms in title, model, or description.",
  "field.keywords.exclude": "Hide broadcasts that contain these words.",
  "field.noise.deduplicate": "Collapse near-duplicate listings of the same item.",
  "field.noise.minRelevance": "Minimum match strength: Exact (strict) → Broad (more results).",

  // Type options
  "opt.type.WTB": "Want to Buy — alerts from buyers seeking these items.",
  "opt.type.WTS": "Want to Sell — alerts from sellers offering these items.",
  "opt.type.Both": "See both buy and sell alerts for comprehensive market coverage.",

  // Condition options
  "opt.condition.NEW": "Brand-new items (retail or bulk).",
  "opt.condition.REFURB": "Refurbished by manufacturer or certified facility.",
  "opt.condition.USED": "Previously used, operational; see description for details.",
  "opt.condition.SELLER_REFURB": "Refurbished by seller with warranty.",
  "opt.condition.PARTS": "Items sold for parts or repair only.",

  // Region options
  "opt.region.US": "United States, Canada, and Mexico.",
  "opt.region.EU": "European Union countries.",
  "opt.region.GB": "United Kingdom and Ireland.",

  // Currency options
  "opt.currency.USD": "US Dollar - most common for US listings.",
  "opt.currency.EUR": "Euro - standard for EU listings.",
  "opt.currency.GBP": "British Pound - used for UK listings.",

  // Relevance options
  "opt.minRelevance.exact": "Exact brand + part/model match preferred.",
  "opt.minRelevance.strong": "Strong signal: brand + model or part prefix.",
  "opt.minRelevance.broad": "Category or keyword match; widest net.",

  // Estimate window options
  "opt.estimateWindow.7": "7 days - short-term market snapshot.",
  "opt.estimateWindow.14": "14 days - weekly trend analysis.",
  "opt.estimateWindow.30": "30 days - monthly market overview.",

  // Section headers
  "section.scope": "Define the basic scope of your alert (type and categories).",
  "section.identity": "Specify exact products through manufacturers, models, and part numbers.",
  "section.quality": "Set quality standards and quantity requirements.",
  "section.pricing": "Configure price filters and currency preferences.",
  "section.geography": "Focus on a specific country.",
  "section.keywords": "Add text-based filters for titles and descriptions.",
  "section.noise": "Control result quality and reduce duplicates.",
  "section.preview": "Configure how estimates are calculated.",
};

/**
 * Safe getter for help text with fallback
 * @param key - The help text key
 * @returns The help text or a fallback message
 */
export const helpFor = (key: string): string => {
  return HELP_TEXT[key] || `Help text for "${key}" is not available.`;
};

/**
 * Get help text for a field with optional sub-key
 * @param field - The field name
 * @param subKey - Optional sub-key (e.g., 'bulkOnly', 'exact')
 * @returns The help text
 */
export const helpForField = (field: string, subKey?: string): string => {
  const key = subKey ? `field.${field}.${subKey}` : `field.${field}`;
  return helpFor(key);
};

/**
 * Get help text for an option value
 * @param category - The option category (e.g., 'type', 'condition')
 * @param value - The option value (e.g., 'WTB', 'NEW')
 * @returns The help text
 */
export const helpForOption = (category: string, value: string): string => {
  const key = `opt.${category}.${value}`;
  return helpFor(key);
};

/**
 * Get help text for a section
 * @param section - The section name
 * @returns The help text
 */
export const helpForSection = (section: string): string => {
  const key = `section.${section}`;
  return helpFor(key);
};
